import { ChevronRight } from 'lucide-react';

import { Badge } from '@client/components/ui/badge';
import { useFeatureFlags } from '@client/contexts/FeatureFlagsContext';
import { LtcCostDisplay } from '@client/components/LtcCostDisplay';
import { cn } from '@client/lib';

export interface ExistingLtcPolicyCardProps {
  label: string;
  hasPolicy: boolean;
  // When set, shown as the policy face amount in the badge
  policyFaceAmount?: number | null;
  // When set and hasPolicy, show as the subtitle
  policyTypeName?: string | null;
  disabled?: boolean;
  onClick: () => void;
}

export function ExistingLtcPolicyCard({
  label,
  hasPolicy,
  policyFaceAmount,
  policyTypeName,
  disabled,
  onClick,
}: ExistingLtcPolicyCardProps) {
  const { flags } = useFeatureFlags();
  const badgeLabel =
    hasPolicy && policyFaceAmount != null && policyFaceAmount > 0 ? (
      <LtcCostDisplay value={policyFaceAmount} />
    ) : hasPolicy ? (
      'Policy added'
    ) : null;

  const subtitle =
    flags.existingLtcPolicyTypeOnCard && hasPolicy && policyTypeName
      ? policyTypeName
      : 'Add or edit policy';

  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        className={cn(
          'flex w-full items-center justify-between px-5 py-4 text-left transition-colors',
          !disabled && 'hover:bg-gray-50',
          disabled && 'cursor-not-allowed opacity-60',
        )}
      >
        <div className="flex flex-col gap-0.5">
          <span className="font-semibold text-gray-900">{label}</span>
          <span className="text-sm text-gray-600">{subtitle}</span>
        </div>
        <div className="flex items-center gap-3">
          {badgeLabel && (
            <Badge
              variant="secondary"
              className="font-medium"
            >
              {badgeLabel}
            </Badge>
          )}
          <ChevronRight className="h-5 w-5 text-gray-500" />
        </div>
      </button>
    </div>
  );
}
